import { useState, useEffect, useMemo } from 'react'
import { Grid, GridItem } from '@truepill/react-capsule'
import { Link, useLazyQuery, useMutation } from '@truepill/tpos-react-router'
import { UserRoles } from '@truepill/tpos-types'
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import { ActionButtonOutline } from 'components/ActionButton'
import AuthLimited from 'components/AuthLimited'
import BreadCrumb from 'components/BreadCrumb'
import IconWrapper, { LargeIconWrapper } from 'components/IconWrapper'
import { CapsulePageContainer, PageContent, CapsulePageHeading, CapsuleTitle } from 'components/PageStructure'
import Pagination from 'components/Pagination'
import type { TableDefinition } from 'components/Table'
import Table from 'components/Table'
import { LIST_SIG_CODES, REMOVE_SIG_CODE } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import useStateWithDebouncedValue from 'hooks/useStateWithDebouncedValue'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { usePlusClient } from 'providers/VisionRouter'
import { capsulePrimaryColorDark } from 'styles/styleVariables'
import type { SigCode } from 'types'
import AddSigCodeModal from './components/AddSigCodeModal'
import { SearchControls, StyledSigCodeSearch, TableWrapper } from './StyledComponents'

interface TableRow {
  _id: string
  code: string
  es?: string
  en: string
  multiplier?: number
}

interface useListSigCodesQueryWithPaginationProps {
  pageSize: number
  partialSigCode?: string
}

interface EditActionButtonProps {
  data: TableRow
}

const useListSigCodesQueryWithPagination = ({ pageSize, partialSigCode }: useListSigCodesQueryWithPaginationProps) => {
  const [results, setResults] = useState<SigCode[]>([])
  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState(pageSize)
  const [totalResults, setTotalResults] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [listSigCodes, { data: sigCodes, loading, error }] = useLazyQuery(LIST_SIG_CODES)

  useEffect(() => {
    if (sigCodes) {
      const totalRecords = sigCodes?.listSigCodes?.totalRecords
      setTotalResults(totalRecords)
      setTotalPages(Math.ceil(totalRecords / limit))
      setResults(sigCodes?.listSigCodes?.records)
    }
  }, [sigCodes, limit])

  useEffect(() => {
    listSigCodes?.({
      variables: {
        pageSize: limit,
        pageNumber: page,
        partialSigCode: partialSigCode,
      },
    })
  }, [listSigCodes, limit, page, partialSigCode])

  useEffect(() => {
    setPage(0)
  }, [partialSigCode])

  return { sigCodes: results, totalResults, totalPages, loading, error, setPage, setLimit, page, limit }
}

const parseSigCodeData = (data?: SigCode[]) => {
  return data
    ? data.map(({ code, translations, _id, multiplier }) => {
        return {
          _id,
          code,
          es: translations.es,
          en: translations.en,
          multiplier: multiplier,
        }
      })
    : []
}

const RemoveActionButton = ({ data }: EditActionButtonProps) => {
  const showErrorToast = useErrorToast()
  const showSuccessToast = useSuccessToast()
  const [removeSigCode] = useMutation(REMOVE_SIG_CODE, {
    refetchQueries: ['listSigCodes'],
    onError: err => {
      showErrorToast(`Failed to remove the Sig code: ${err.message}`)
    },
    onCompleted: () => {
      showSuccessToast('Sig code removed successfully.')
    },
  })

  return (
    <LargeIconWrapper>
      <DeleteIcon
        style={{ cursor: 'pointer' }}
        fill={capsulePrimaryColorDark}
        onClick={() => {
          removeSigCode({
            variables: {
              id: data._id,
            },
          })
        }}
      />
    </LargeIconWrapper>
  )
}

const EditActionButton = ({ data }: EditActionButtonProps) => {
  return (
    <LargeIconWrapper>
      <Link to={`/pharmacy/sigCodes/${data.code}`}>
        <EditIcon style={{ cursor: 'pointer' }} fill={capsulePrimaryColorDark} />
      </Link>
    </LargeIconWrapper>
  )
}

const tableDataDefinition: TableDefinition<TableRow>[] = [
  { field: 'code', headerName: 'Sig code', width: '1fr', minWidth: '12rem' },
  { field: 'en', headerName: 'English directions', width: '2fr', minWidth: '30rem' },
  { field: 'es', headerName: 'Spanish translation', width: '2fr', minWidth: '30rem' },
  { field: 'multiplier', headerName: 'DS multiplier', width: '2fr', minWidth: '10rem' },
]

const PharmacySigCodes = (): JSX.Element => {
  const { showModal } = useModalContext()
  const [sigCodeSearchTerm, debouncedSigSearchTerm, setSigCodeSearchTerm] = useStateWithDebouncedValue('')
  const {
    sigCodes,
    loading,
    setPage,
    setLimit,
    totalPages,
    totalResults,
    page: currentPage,
    limit,
  } = useListSigCodesQueryWithPagination({
    pageSize: 10,
    partialSigCode: debouncedSigSearchTerm,
  })
  const { tokenContext } = usePlusClient()
  const { hasRole } = tokenContext
  const isUserAdmin = hasRole && hasRole([UserRoles.Admin])

  const tableDefinition = useMemo(() => {
    const baseDefinition = [...tableDataDefinition]
    if (isUserAdmin) {
      baseDefinition.push({
        headerName: 'Actions',
        minWidth: '4rem',
        width: '4rem',
        customRender: (data: TableRow) => (
          <Grid>
            <GridItem>
              <EditActionButton data={data} />
            </GridItem>
            <GridItem>
              <RemoveActionButton data={data} />
            </GridItem>
          </Grid>
        ),
      })
    }
    return baseDefinition
  }, [isUserAdmin])

  return (
    <CapsulePageContainer>
      <BreadCrumb useCapsuleDesign />
      <CapsulePageHeading>
        <CapsuleTitle>Sig code library</CapsuleTitle>
        <AuthLimited roles={[UserRoles.Admin]}>
          <div>
            <ActionButtonOutline
              icon={
                <IconWrapper>
                  <PlusIcon />
                </IconWrapper>
              }
              hotKey="n"
              label="Add sig code"
              onClick={() => {
                showModal(() => <AddSigCodeModal />)
              }}
            />
          </div>
        </AuthLimited>
      </CapsulePageHeading>
      <SearchControls>
        <StyledSigCodeSearch
          data-private
          placeholder="Search sig codes..."
          value={sigCodeSearchTerm}
          onChange={({ currentTarget: { value } }) => {
            setSigCodeSearchTerm(value)
          }}
        />
      </SearchControls>
      <PageContent>
        <TableWrapper>
          <Table
            rowStyle={{ alignItems: 'center' }}
            definition={tableDefinition}
            data={parseSigCodeData(sigCodes)}
            loading={loading}
            keyField="code"
          />
          <Pagination
            onChangePage={setPage}
            totalRecords={totalResults}
            currentPage={currentPage}
            totalPages={totalPages}
            pagesForEachSide={1}
            rowsPerPage={limit}
            rowsPerPageOptions={[10, 25, 50]}
            onChangeRowsPerPage={setLimit}
          />
        </TableWrapper>
      </PageContent>
    </CapsulePageContainer>
  )
}

export default PharmacySigCodes
